import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooterProgressBar } from 'ui-components/components/modal/modal-footer/modal-footer-progress-bar';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import { SPLIT_SHIP_ACTIONS, useTrackSplitShipAction } from './event-tracking/use-track-split-ship-action';
import ItemsTable from './items-table';
import { confirmDialogMessages, modalMessages } from './messages';
import { useSplitShipModalContext } from './split-ship-modal-context-provider';
import { SplitShipModalFooter } from './split-ship-modal-footer';
import style from './split-ship-modal.module.less';
import ModalPortal from '../../components/modal-portal';
import { ConfirmDialogueModal } from '../../components/modals/confirm-dialogue-modal';
import { useEntitiesSalesOrder } from '../../hooks/entities/selectors/sales-orders';
import { useModal } from '../../hooks/use-modal';

export const SplitShipModalContent = ({ isInitializing, onClose, salesOrderId }) => {
  const { orderNumber } = useEntitiesSalesOrder(salesOrderId) || {};
  const { hasMadeChanges, isSaving, saveFailed } = useSplitShipModalContext();
  const { close: closeDialog, isOpen, open: openDialog } = useModal();
  const [openModalLogged, setOpenModalLogged] = useState(false);

  const trackModalOpen = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.MODAL_OPEN);
  const trackModalClose = useTrackSplitShipAction(SPLIT_SHIP_ACTIONS.MODAL_CLOSE);

  if (!isInitializing && !openModalLogged) {
    trackModalOpen();
    setOpenModalLogged(true);
  }

  const onCloseSplitShipModal = useCallback(() => {
    onClose();
    trackModalClose();
  }, [onClose, trackModalClose]);

  const onCloseCallback = useCallback(() => {
    if (hasMadeChanges) {
      openDialog();
    } else {
      onCloseSplitShipModal();
    }
  }, [hasMadeChanges, openDialog, onCloseSplitShipModal]);

  return (
    <>
      <ModalPortal onClose={onCloseCallback}>
        <Modal className={style['split-ship-modal']} size='large'>
          <ModalHeader closeModal={onCloseCallback}>
            <FormattedMessage {...modalMessages.splitShipModalHeader} values={{ orderNumber }} />
          </ModalHeader>
          <ModalBody className={style.body}>
            {saveFailed ? (
              <BannerMessageV2
                className={style['error-banner']}
                content={<FormattedMessage {...modalMessages.defaultErrorMessage} />}
                status={BannerStatus.Error}
              />
            ) : null}
            <div className={classnames({ [style.hidden]: isInitializing })}>
              <p className={style.instructions}>
                <FormattedMessage {...modalMessages.splitShipMoveInstructions} />
              </p>
              <p className={style.instructions}>
                <FormattedMessage {...modalMessages.splitShipSplitInstructions} />
              </p>
              <h4 className={style['table-title']}>
                <FormattedMessage {...modalMessages.tableTitle} />
              </h4>
              <ItemsTable />
            </div>
            {isInitializing ? <LoadingIndicator className={style['loading-indicator']} /> : null}
          </ModalBody>
          {isSaving ? <ModalFooterProgressBar /> : null}
          <SplitShipModalFooter onClose={onClose} />
        </Modal>
      </ModalPortal>
      {isOpen ? (
        <ConfirmDialogueModal
          body={
            <>
              <FormattedMessage {...confirmDialogMessages.confirmDialogCancelDescription} />
              <br />
              <FormattedMessage {...confirmDialogMessages.confirmDialogContinueDescription} />
            </>
          }
          header={<FormattedMessage {...confirmDialogMessages.confirmDialogTitle} />}
          onClose={closeDialog}
          onConfirm={onCloseSplitShipModal}
        />
      ) : null}
    </>
  );
};

SplitShipModalContent.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  salesOrderId: PropTypes.string.isRequired,
};
